import type {
  CancelCustomJobDTO,
  CreateCustomJobDTO,
  CustomJobBoardQueryDTO,
  CustomJobListQueryDTO,
  UpdateCustomJobDTO,
} from "@/modules/custom-jobs/domain/contracts";
import type {
  CustomJobDetailDTO,
  CustomJobBoardResult,
} from "@/modules/custom-jobs/domain/types";
import type { OffsetPageDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type CustomJobsListResult = OffsetPageDTO<
  import("@/modules/custom-jobs/domain/types").CustomJobListItemDTO
>;

export const customJobsApi = {
  async board(query?: CustomJobBoardQueryDTO): Promise<CustomJobBoardResult> {
    const url = new URL(`${config.apiUrl}/custom-jobs/board`);
    url.searchParams.set("scope", query?.scope ?? "all");
    return fetcher.get(url.toString());
  },
  async list(query?: CustomJobListQueryDTO): Promise<CustomJobsListResult> {
    const url = new URL(`${config.apiUrl}/custom-jobs`);
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.overdue) url.searchParams.set("overdue", "true");
    if (query?.dueBefore) url.searchParams.set("dueBefore", query.dueBefore);
    if (query?.dueAfter) url.searchParams.set("dueAfter", query.dueAfter);
    url.searchParams.set("limit", String(query?.limit ?? 20));
    url.searchParams.set("offset", String(query?.offset ?? 0));
    return fetcher.get(url.toString());
  },
  async get(id: string): Promise<CustomJobDetailDTO> {
    return fetcher.get(`${config.apiUrl}/custom-jobs/${id}`);
  },
  async create(input: CreateCustomJobDTO): Promise<CustomJobDetailDTO> {
    return fetcher.post(`${config.apiUrl}/custom-jobs`, input);
  },
  async update(
    id: string,
    input: UpdateCustomJobDTO,
  ): Promise<CustomJobDetailDTO> {
    return fetcher.patch(`${config.apiUrl}/custom-jobs/${id}`, input);
  },
  async confirm(id: string): Promise<CustomJobDetailDTO> {
    return fetcher.post(`${config.apiUrl}/custom-jobs/${id}/confirm`, {});
  },
  async startWork(id: string): Promise<CustomJobDetailDTO> {
    return fetcher.post(`${config.apiUrl}/custom-jobs/${id}/start-work`, {});
  },
  async markReady(id: string): Promise<CustomJobDetailDTO> {
    return fetcher.post(`${config.apiUrl}/custom-jobs/${id}/mark-ready`, {});
  },
  async complete(id: string): Promise<CustomJobDetailDTO> {
    return fetcher.post(`${config.apiUrl}/custom-jobs/${id}/complete`, {});
  },
  async cancel(
    id: string,
    input?: CancelCustomJobDTO,
  ): Promise<CustomJobDetailDTO> {
    return fetcher.post(`${config.apiUrl}/custom-jobs/${id}/cancel`, input ?? {});
  },
};
